import * as THREE from 'three';
import { state } from './state.js';
import { initScene, applyStyle, applyPavement, updateZSpacing } from './scene-setup.js';
import {
  getActiveCamera,
  setCameraType,
  setCameraMode,
  setCameraZoom,
  setCameraHeight,
  setCameraPanX,
  setFloorHeight
} from './camera-controls.js';
import { spawnAvatar } from './avatar-controller.js';
import { selectProject, loadProjectIntoScene, fetchProjects } from './project-io.js';
import { initUI } from './ui.js';
import { ensureCanvasFonts } from '../core/fonts.js';

// Expose các test & automation hooks toàn cục
window.selectProject = selectProject;
window.loadProjectIntoScene = loadProjectIntoScene;
window.switchCameraType = setCameraType;
window.applyStyle = applyStyle;
window.updateZSpacing = updateZSpacing;
window.applyPavement = applyPavement;
window.spawnAvatar = spawnAvatar;
window.setFloorHeight = setFloorHeight;
window.setCameraZoom = setCameraZoom;
window.setCameraHeight = setCameraHeight;
window.setCameraPanX = setCameraPanX;
window.state = state;
window.THREE = THREE;

// Khởi tạo vòng lặp hoạt ảnh (Animation Loop)
const clock = new THREE.Clock();
let frameCount = 0;
let lastFpsTime = performance.now();
const hudFps = document.getElementById('hud-fps');
const hudCamPos = document.getElementById('hud-cam-pos');

function animate() {
  requestAnimationFrame(animate);

  const isPaused = Boolean(window.__PAUSE_ANIMATION__);
  const delta = isPaused ? 0 : clock.getDelta();
  const elapsedTime = isPaused ? 0 : clock.getElapsedTime();

  // FPS Counter
  frameCount++;
  const now = performance.now();
  if (now - lastFpsTime >= 500) {
    const fps = Math.round((frameCount * 1000) / (now - lastFpsTime));
    if (hudFps) hudFps.textContent = `${fps} FPS`;
    frameCount = 0;
    lastFpsTime = now;
  }

  // Cập nhật Avatar
  if (state.currentAvatar && !isPaused) {
    state.currentAvatar.update(delta);
  }

  // Cyberpunk Neon Pulse
  if (state.currentStyleKey === 'cyber' && state.cyanPointLight && state.magentaPointLight) {
    const pulse = isPaused ? 1.0 : Math.sin(elapsedTime * 4.0) * 0.4 + 1.0;
    state.cyanPointLight.intensity = 3.5 * pulse;
    state.magentaPointLight.intensity = 4.0 * (1.8 - pulse * 0.8);
  }

  const activeCam = getActiveCamera();

  // Cập nhật vị trí camera theo từng chế độ
  if (isPaused) {
    const targetCamX = state.manualCamPanX;
    const targetCamY = state.manualCamHeight;
    const targetCamZ = state.currentCameraType === 'orthographic' ? 16.0 : 16.0 / (state.manualCamZoom * 0.8 + 0.2);
    activeCam.position.set(targetCamX, targetCamY, targetCamZ);
    const lookTargetX = state.currentCameraType === 'orthographic' ? targetCamX * 0.28 : state.manualCamPanX * 0.4;
    const lookTargetY = state.manualCamHeight - 1.1 + state.currentFloorHeight * 0.5;
    activeCam.lookAt(lookTargetX, lookTargetY, 0);
  } else if (state.currentCameraMode === 'parallax') {
    state.mouse.x += (state.mouse.targetX - state.mouse.x) * 0.06;
    state.mouse.y += (state.mouse.targetY - state.mouse.y) * 0.06;

    const swayX = state.isAutoSway ? Math.sin(elapsedTime * 0.65) * 1.2 : 0;
    const swayY = state.isAutoSway ? Math.cos(elapsedTime * 0.45) * 0.3 : 0;

    const targetCamX = state.manualCamPanX + state.mouse.x * 4.0 + swayX;
    const targetCamY = state.manualCamHeight + state.mouse.y * 1.0 + swayY;
    const targetCamZ = state.currentCameraType === 'orthographic' ? 16.0 : 16.0 / (state.manualCamZoom * 0.8 + 0.2);

    activeCam.position.x += (targetCamX - activeCam.position.x) * 0.055;
    activeCam.position.y += (targetCamY - activeCam.position.y) * 0.055;
    activeCam.position.z += (targetCamZ - activeCam.position.z) * 0.055;

    const lookTargetX = state.currentCameraType === 'orthographic' ? targetCamX * 0.28 : state.manualCamPanX * 0.4;
    const lookTargetY = state.manualCamHeight - 1.1 + state.currentFloorHeight * 0.5 + state.mouse.y * 0.3;
    activeCam.lookAt(lookTargetX, lookTargetY, 0);
  } else if (state.currentCameraMode === 'side') {
    const sideTargetX = 26.0;
    const sideTargetY = 4.2;
    const sideTargetZ = 0.0;

    activeCam.position.x += (sideTargetX - activeCam.position.x) * 0.08;
    activeCam.position.y += (sideTargetY - activeCam.position.y) * 0.08;
    activeCam.position.z += (sideTargetZ - activeCam.position.z) * 0.08;

    activeCam.lookAt(0, 3.8, 0);
  } else if (state.currentCameraMode === 'orbit' && state.controls) {
    state.controls.update();
  }

  // HUD Tọa độ
  if (hudCamPos) {
    hudCamPos.textContent = `X: ${activeCam.position.x.toFixed(1)} | Y: ${activeCam.position.y.toFixed(1)} | Z: ${activeCam.position.z.toFixed(1)}`;
  }

  if (state.renderer && state.scene) {
    state.renderer.render(state.scene, activeCam);
  }
}

// Khởi chạy hệ thống
const canvas = document.getElementById('webgl-canvas');
initScene(canvas);
initUI();
ensureCanvasFonts();

applyPavement('terrazzo');
applyStyle('ghibli');
updateZSpacing(1.0);
setCameraType('orthographic');
setCameraMode('parallax');
spawnAvatar('CO_GAI_AO_DAI', 'walk');
animate();

fetchProjects();
